/**
 * ModuleLauncher — Dynamic module launcher that loads modules from live database.
 * 
 * Replaces static NAVIGATION_ITEMS with live API data from /api/menu/modules.
 * Falls back to static data only if API fails.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, ArrowRight, Grid, Loader2, RefreshCw, Home, DollarSign, Truck, ShoppingCart, Package, Store, Users, Settings, Database, FileText, Globe, Hash, Cog, BarChart3, Warehouse, CreditCard, Briefcase, Layers, LayoutList } from 'lucide-react';
import api from '@/services/api';
import { ModuleId } from '@/types';
import { useLicense } from '@/context/LicenseContext';
import { SCREEN_ID_TO_MODULE } from '@/components/Sidebar';

// Dynamic icon resolver — matches module name keywords to icons
const getModuleIconByName = (name, size = 20) => {
  if (!name) return <Hash size={size} />;
  const n = name.toLowerCase();
  if (n.includes('dash')) return <Home size={size} />;
  if (n.includes('financ') || n.includes('account')) return <DollarSign size={size} />;
  if (n.includes('logist') || n.includes('transport')) return <Truck size={size} />;
  if (n.includes('procure') || n.includes('purchase')) return <ShoppingCart size={size} />;
  if (n.includes('inventory') || n.includes('stock')) return <Package size={size} />;
  if (n.includes('pos') || n.includes('billing') || n.includes('store')) return <Store size={size} />;
  if (n.includes('payroll') || n.includes('salary') || n.includes('hr')) return <Users size={size} />;
  if (n.includes('warehouse')) return <Warehouse size={size} />;
  if (n.includes('setup') || n.includes('setting') || n.includes('config')) return <Cog size={size} />;
  if (n.includes('system') || n.includes('manager')) return <Settings size={size} />;
  if (n.includes('data') || n.includes('database')) return <Database size={size} />;
  if (n.includes('report')) return <BarChart3 size={size} />;
  if (n.includes('web') || n.includes('server')) return <Globe size={size} />;
  if (n.includes('roll') || n.includes('role')) return <Briefcase size={size} />;
  if (n.includes('replen') || n.includes('replan')) return <RefreshCw size={size} />;
  if (n.includes('payment') || n.includes('credit')) return <CreditCard size={size} />;
  if (n.includes('license') || n.includes('about')) return <FileText size={size} />;
  if (n.includes('button') || n.includes('active') || n.includes('menu')) return <Layers size={size} />;
  return <Hash size={size} />;
};

// Map module IDs to ModuleId constants for navigation
const MODULE_ID_TO_LAUNCH = {
  1: ModuleId.DASHBOARD,
  2: 'FINANCE',
  3: ModuleId.LOGISTICS,
  4: 'PROCUREMENT',
  5: 'INVENTORY',
  6: 'POS',
  7: ModuleId.PAYROLL,
  8: 'PRODUCTION',
  // Setup modules (20-27)
  20: 'MYACCOUNTS_SETUP',
  21: 'SYSTEM_MANAGER',
  22: 'APP_SETUP',
  23: 'DATA_MGMT',
  24: 'FINAL_ACCOUNTS',
  25: 'WEB_SETUP',
  26: 'SETTINGS_REPORTS',
};

// Get module category based on ID
const getModuleCategory = (moduleId, moduleName) => {
  const name = (moduleName || '').toLowerCase();
  if (moduleId === 1) return 'General';
  if (moduleId === 2) return 'Finance';
  if ([3, 4, 5, 8].includes(moduleId)) return 'Operations';
  if (moduleId === 6) return 'Sales';
  if (moduleId === 7) return 'HR';
  if (moduleId >= 20 && moduleId <= 27) return 'Setup';
  if (name.includes('admin') || name.includes('system')) return 'Admin';
  if (name.includes('tool')) return 'Tools';
  return 'General';
};

// Fallback static modules (only used if API fails)
const FALLBACK_MODULES = [
  { MODULE_ID: 1, MODULE_NAME: 'Dashboard' },
  { MODULE_ID: 2, MODULE_NAME: 'Financial Accounting' },
  { MODULE_ID: 3, MODULE_NAME: 'Logistics' },
  { MODULE_ID: 4, MODULE_NAME: 'Procurement' },
  { MODULE_ID: 5, MODULE_NAME: 'Inventory' },
  { MODULE_ID: 6, MODULE_NAME: 'POS System' },
  { MODULE_ID: 7, MODULE_NAME: 'MyPayroll' },
];

const ModuleLauncher = ({ isOpen, onClose, onLaunch, theme = 'dark' }) => {
  const [query, setQuery] = useState('');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTab, setSearchTab] = useState('modules'); // 'modules' | 'screens'
  const [screens, setScreens] = useState([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [screensError, setScreensError] = useState(null);
  const isDark = theme === 'dark';
  const { isModuleAllowed, isScreenAllowed, accessUnrestricted } = useLicense();

  // Fetch ALL navigable screens (ID + Name search) — only screens with a known
  // frontend destination (present in SCREEN_ID_TO_MODULE) are shown so results
  // always land somewhere real.
  const fetchScreens = useCallback(async () => {
    setScreensLoading(true);
    setScreensError(null);
    try {
      const response = await api.get('/api/menu/screens');
      if (response.data.success) {
        const navigable = (response.data.data || [])
          .filter(s => SCREEN_ID_TO_MODULE[s.SCREEN_ID])
          .map(s => ({
            screenId: s.SCREEN_ID,
            moduleId: SCREEN_ID_TO_MODULE[s.SCREEN_ID],
            title: s.SCREEN_TITLE,
          }));
        setScreens(navigable);
      } else {
        setScreensError('Failed to load screens');
      }
    } catch (err) {
      console.error('Error fetching screens for launcher:', err);
      setScreensError('Failed to load screens');
    } finally {
      setScreensLoading(false);
    }
  }, []);

  // Fetch modules from API
  const fetchModules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/menu/modules');
      if (response.data.success && response.data.data?.length > 0) {
        // Transform API data to launcher format
        const apiModules = response.data.data.map(m => ({
          id: MODULE_ID_TO_LAUNCH[m.MODULE_ID] || m.MODULE_NAME?.toUpperCase().replace(/\s+/g, '_') || `MODULE_${m.MODULE_ID}`,
          moduleId: m.MODULE_ID,
          label: m.MODULE_NAME,
          icon: getModuleIconByName(m.MODULE_NAME),
          category: getModuleCategory(m.MODULE_ID, m.MODULE_NAME),
          description: `Access ${m.MODULE_NAME} module and its features.`,
        }));
        setModules(apiModules);
      } else {
        // Use fallback if API returns empty
        setModules(FALLBACK_MODULES.map(m => ({
          id: MODULE_ID_TO_LAUNCH[m.MODULE_ID] || m.MODULE_NAME?.toUpperCase().replace(/\s+/g, '_'),
          moduleId: m.MODULE_ID,
          label: m.MODULE_NAME,
          icon: getModuleIconByName(m.MODULE_NAME),
          category: getModuleCategory(m.MODULE_ID, m.MODULE_NAME),
          description: `Access ${m.MODULE_NAME} module and its features.`,
        })));
      }
    } catch (err) {
      console.error('Error fetching modules for launcher:', err);
      setError('Failed to load modules');
      // Use fallback on error
      setModules(FALLBACK_MODULES.map(m => ({
        id: MODULE_ID_TO_LAUNCH[m.MODULE_ID] || m.MODULE_NAME?.toUpperCase().replace(/\s+/g, '_'),
        moduleId: m.MODULE_ID,
        label: m.MODULE_NAME,
        icon: getModuleIconByName(m.MODULE_NAME),
        category: getModuleCategory(m.MODULE_ID, m.MODULE_NAME),
        description: `Access ${m.MODULE_NAME} module and its features.`,
      })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSearchTab('modules');
      fetchModules();
      fetchScreens();
    }
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, fetchModules, fetchScreens]);

  if (!isOpen) return null;

  const filtered = modules
    .filter(item => accessUnrestricted || isModuleAllowed(item.moduleId))
    .filter((item) =>
      item.label?.toLowerCase().includes(query.toLowerCase()) ||
      item.category?.toLowerCase().includes(query.toLowerCase())
    );

  const filteredScreens = screens
    .filter(s => accessUnrestricted || isScreenAllowed(s.screenId))
    .filter((s) =>
      s.title?.toLowerCase().includes(query.toLowerCase()) ||
      s.screenId?.toLowerCase().includes(query.toLowerCase())
    );

  return (
    <div data-testid="module-launcher" className="fixed inset-0 bg-black/60 backdrop-blur-md z-[200] flex items-center justify-center p-3 sm:p-6 transition-all">
      <div className={`w-full max-w-4xl max-h-[90vh] flex flex-col rounded-[32px] shadow-2xl overflow-hidden border ${isDark ? 'bg-[#0f0f0f] border-[#222]' : 'bg-white border-gray-200'}`}>
        <div className={`p-5 sm:p-8 border-b flex-shrink-0 ${isDark ? 'border-[#222]' : 'border-gray-100'}`}>
          <div className="flex items-center gap-6">
            <div className="p-4 rounded-2xl bg-blue-600 text-white shadow-xl shadow-blue-600/20"><Grid size={24} /></div>
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="launcher-search"
                autoFocus type="text"
                placeholder={searchTab === 'modules' ? 'Search modules...' : 'Search by Screen ID or Name...'}
                className={`w-full bg-transparent border-none outline-none text-xl font-bold pl-10 pr-10 ${isDark ? 'text-white placeholder:text-gray-700' : 'text-gray-900 placeholder:text-gray-400'}`}
                value={query} onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button 
              data-testid="launcher-refresh" 
              onClick={searchTab === 'modules' ? fetchModules : fetchScreens}
              className={`p-3 rounded-2xl transition-all ${isDark ? 'hover:bg-[#1a1a1a] text-gray-500' : 'hover:bg-gray-50 text-gray-400'}`}
              title="Refresh"
            >
              <RefreshCw size={18} className={(loading || screensLoading) ? 'animate-spin' : ''} />
            </button>
            <button data-testid="launcher-close-btn" onClick={onClose} className={`p-3 rounded-2xl transition-all ${isDark ? 'hover:bg-[#1a1a1a] text-gray-500' : 'hover:bg-gray-50 text-gray-400'}`}><X size={20} /></button>
          </div>

          {/* Modules / Screens segmented toggle */}
          <div className={`mt-5 inline-flex rounded-xl p-1 gap-1 ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`} data-testid="launcher-search-tabs">
            {[
              { id: 'modules', label: 'Modules', icon: Grid },
              { id: 'screens', label: 'Screens', icon: LayoutList },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = searchTab === tab.id;
              return (
                <button
                  key={tab.id}
                  data-testid={`launcher-tab-${tab.id}`}
                  onClick={() => setSearchTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white shadow'
                      : isDark ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <Icon size={13} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-5 sm:p-8 min-h-0">
          {searchTab === 'modules' ? (
          loading ? (
            <div className="py-20 text-center space-y-4">
              <Loader2 size={32} className="mx-auto text-blue-500 animate-spin" />
              <p className={`font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading modules from database...</p>
            </div>
          ) : error ? (
            <div className="py-20 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 mx-auto flex items-center justify-center"><Search size={24} /></div>
              <p className="font-bold text-red-400">{error}</p>
              <button onClick={fetchModules} className="text-blue-500 underline text-sm">Retry</button>
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => (
                <button
                  key={item.moduleId}
                  data-testid={`launcher-module-${String(item.id).toLowerCase()}`}
                  onClick={() => { onLaunch(item.id); onClose(); }}
                  className={`flex flex-col p-6 rounded-[24px] border transition-all text-left group hover:scale-[1.02] ${isDark ? 'bg-[#151515] border-[#222] hover:border-blue-500/50 hover:bg-[#1a1a1a]' : 'bg-gray-50 border-gray-100 hover:border-blue-500 hover:bg-white'}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-6 transition-colors ${isDark ? 'bg-black text-blue-400 group-hover:bg-blue-600 group-hover:text-white' : 'bg-white text-blue-600 group-hover:bg-blue-600 group-hover:text-white shadow-sm'}`}>
                    {item.icon}
                  </div>
                  <div className="space-y-1.5">
                    <p className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.category}</p>
                    <h3 className={`font-black text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.label}</h3>
                  </div>
                  <p className="mt-4 text-[10px] text-gray-500 font-medium leading-relaxed">{item.description}</p>
                  <div className="mt-6 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Launch Module</span>
                    <ArrowRight size={14} className="text-blue-500" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 mx-auto flex items-center justify-center"><Search size={24} /></div>
              <p className="font-bold text-gray-400">No modules matched your query.</p>
            </div>
          )
          ) : (
          screensLoading ? (
            <div className="py-20 text-center space-y-4">
              <Loader2 size={32} className="mx-auto text-blue-500 animate-spin" />
              <p className={`font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading screens...</p>
            </div>
          ) : screensError ? (
            <div className="py-20 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 mx-auto flex items-center justify-center"><Search size={24} /></div>
              <p className="font-bold text-red-400">{screensError}</p>
              <button onClick={fetchScreens} className="text-blue-500 underline text-sm">Retry</button>
            </div>
          ) : filteredScreens.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="launcher-screens-results">
              {filteredScreens.slice(0, 60).map((s) => (
                <button
                  key={s.screenId}
                  data-testid={`launcher-screen-${s.screenId}`}
                  onClick={() => { onLaunch(s.moduleId); onClose(); }}
                  className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left group hover:scale-[1.02] ${isDark ? 'bg-[#151515] border-[#222] hover:border-blue-500/50 hover:bg-[#1a1a1a]' : 'bg-gray-50 border-gray-100 hover:border-blue-500 hover:bg-white'}`}
                >
                  <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center font-mono text-[10px] font-black ${isDark ? 'bg-black text-blue-400 group-hover:bg-blue-600 group-hover:text-white' : 'bg-white text-blue-600 group-hover:bg-blue-600 group-hover:text-white shadow-sm'}`}>
                    <Hash size={12} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[9px] font-mono font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.screenId}</p>
                    <h3 className={`font-bold text-xs truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.title}</h3>
                  </div>
                  <ArrowRight size={13} className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 mx-auto flex items-center justify-center"><Search size={24} /></div>
              <p className="font-bold text-gray-400">No screens matched your query.</p>
            </div>
          )
          )}
        </div>
        
        <div className={`p-6 px-8 border-t flex justify-between items-center text-[10px] font-black uppercase tracking-[0.2em] ${isDark ? 'bg-[#0a0a0a] border-[#222] text-gray-600' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${(loading || screensLoading) ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`}></div> 
              {searchTab === 'modules'
                ? (loading ? 'Syncing...' : `${modules.length} Modules Loaded`)
                : (screensLoading ? 'Syncing...' : `${screens.length} Screens Indexed`)}
            </span>
          </div>
          <div className="flex items-center gap-2">RetailCLIQ <span className="text-blue-500 font-mono">LIVE_DB</span></div>
        </div>
      </div>
    </div>
  );
};

export default ModuleLauncher;
